var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { defaultPageSize } from "healthex-common/constants";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { filterNull, getFullName, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { ConfirmUserDeleteDialogModel } from "./ConfirmUserDeleteDialog";
export class AdminUserListViewModel {
    constructor(container) {
        this.deleteConfirmationDialog = new ConfirmUserDeleteDialogModel();
        this.editingUserDialogOpen = false;
        this.editingUser = null;
        this.inviteUsersDialogOpen = false;
        this.page = 0;
        this.totalUsers = 0;
        this.users = [];
        this.organizationsManager = container.organizationsManager;
        this.authManager = container.authManager;
        this.bannerUIManager = container.bannerUIManager;
        void this.loadUsersForCurrentPage();
    }
    async loadUsers(paginationParams) {
        if (!this.authManager.orgId)
            return;
        const start = paginationParams.page * paginationParams.pageSize;
        const end = paginationParams.page * paginationParams.pageSize + paginationParams.pageSize;
        const { results, total } = await this.organizationsManager.loadUsersByOrganization(this.authManager.orgId, start, end);
        this.totalUsers = total;
        this.users = results;
    }
    async loadUsersForCurrentPage() {
        return await this.loadUsers({ page: this.page, pageSize: defaultPageSize });
    }
    async updatePagination(paginationModel) {
        this.page = paginationModel.page;
        await this.loadUsers(paginationModel);
    }
    inviteUsers() {
        this.inviteUsersDialogOpen = true;
    }
    editUser(user) {
        this.editingUser = user;
        this.editingUserDialogOpen = true;
    }
    userEditDone() {
        var _a;
        this.editingUserDialogOpen = false;
        void this.loadUsersForCurrentPage();
        if (((_a = this.editingUser) === null || _a === void 0 ? void 0 : _a.id) === this.authManager.id) {
            void this.authManager.regenerateToken();
        }
    }
    async inviteUsersDone() {
        void this.loadUsersForCurrentPage();
        this.inviteUsersDialogOpen = false;
    }
    getPermissionsDescriptions(permissions) {
        if (this.authManager.userType === null)
            return [];
        switch (this.authManager.userType) {
            case UserType.HEALTHCARE_ORGANIZATION:
                return filterNull(permissions.map((permission) => {
                    switch (permission) {
                        case UserPermissions.ORG_ADMIN:
                            return "IT admin";
                        case UserPermissions.RESEARCH_ADMIN:
                            return "Research admin";
                        case UserPermissions.RESEARCHER:
                            return "Researcher";
                        case UserPermissions.PROVIDER:
                            return "Provider";
                        default:
                            return null;
                    }
                }));
            case UserType.EXTERNAL_RESEARCHER:
                return filterNull(permissions.map((permission) => {
                    switch (permission) {
                        case UserPermissions.ORG_ADMIN:
                            return "Admin";
                        case UserPermissions.RESEARCHER:
                            return "User";
                        default:
                            return null;
                    }
                }));
            case UserType.SERVICE_PROVIDER:
                return filterNull(permissions.map((permission) => {
                    switch (permission) {
                        case UserPermissions.ORG_ADMIN:
                            return "Admin";
                        default:
                            return null;
                    }
                }));
            default:
                return [];
        }
    }
    isSelf(user) {
        return this.authManager.id === user.id;
    }
    getCannotBeDeletedReason(user) {
        const reason = this.organizationsManager.getCannotBeDeletedReason(user);
        if (reason === NOT_LOADED)
            return null;
        switch (reason) {
            case "SELF":
                return "You can’t remove yourself from the organization. Please ask another IT admin to complete this action.";
            case "ONLY_ORG_AND_RESEARCH_ADMIN": {
                return "You can’t remove this user as they’re the only reasearch and IT admin.";
            }
            case "ONLY_ORG_ADMIN": {
                return "You can’t remove this user as they’re the only IT admin.";
            }
            case "ONLY_RESEARCH_ADMIN": {
                return "You can’t remove this user as they’re the only research admin.";
            }
            case "ONLY_RESEARCHER_WITH_AT_LEAST_ONE_STUDY": {
                return "You can’t remove this user as they’re the only researcher.";
            }
            case null: {
                return null;
            }
        }
        return willNeverHappen(reason);
    }
    async deleteUser(user) {
        if (!this.authManager.orgId)
            return;
        this.deleteConfirmationDialog.user = user;
        const confirmed = await this.deleteConfirmationDialog.showAndWait();
        if (confirmed) {
            await this.organizationsManager.deleteUser(this.authManager.orgId, user.id);
            void this.loadUsersForCurrentPage();
            this.bannerUIManager.displayBanner({ text: `${getFullName(user) || user.email}'s account successfully deleted` });
        }
    }
    get orgName() {
        return this.authManager.orgName;
    }
}
__decorate([
    observable
], AdminUserListViewModel.prototype, "editingUserDialogOpen", void 0);
__decorate([
    observable
], AdminUserListViewModel.prototype, "editingUser", void 0);
__decorate([
    observable
], AdminUserListViewModel.prototype, "inviteUsersDialogOpen", void 0);
__decorate([
    observable
], AdminUserListViewModel.prototype, "page", void 0);
__decorate([
    observable
], AdminUserListViewModel.prototype, "totalUsers", void 0);
__decorate([
    observable
], AdminUserListViewModel.prototype, "users", void 0);
__decorate([
    computed
], AdminUserListViewModel.prototype, "orgName", null);
